import React, { useState } from 'react';
import { X, Mail, UserPlus, Link2, Check, Copy } from 'lucide-react';
import { useOrganization, OrganizationInvitation } from '../../contexts/OrganizationContext';
import { Spinner } from '../../design-system';
import toast from '../../design-system/toast';

interface InviteMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  organizationName: string;
}

type InviteMethod = 'email' | 'link';

const InviteMemberModal: React.FC<InviteMemberModalProps> = ({
  isOpen,
  onClose,
  organizationId,
  organizationName,
}) => {
  const { inviteMember } = useOrganization();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'member' | 'viewer'>('member');
  const [inviteMethod, setInviteMethod] = useState<InviteMethod>('email');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createdInvitation, setCreatedInvitation] = useState<OrganizationInvitation | null>(null);
  const [copied, setCopied] = useState(false);

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedEmail) {
      setError('Email address is required');
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const sendEmail = inviteMethod === 'email';
      const invitation = await inviteMember(organizationId, trimmedEmail, role, sendEmail);

      if (inviteMethod === 'email') {
        toast.success(`Invitation sent to ${trimmedEmail}`);
        handleClose();
      } else {
        // Show the link for copying
        setCreatedInvitation(invitation);
        toast.success('Invitation created! Copy the link to share.');
      }
    } catch (err: any) {
      console.error('Failed to create invitation:', err);
      const errorMessage = err.data?.detail || err.message || 'Failed to create invitation. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyLink = async () => {
    if (!createdInvitation?.invite_url) return;

    try {
      await navigator.clipboard.writeText(createdInvitation.invite_url);
      setCopied(true);
      toast.success('Invite link copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      toast.error('Failed to copy link');
    }
  };

  const handleClose = () => {
    setEmail('');
    setRole('member');
    setInviteMethod('email');
    setError(null);
    setCreatedInvitation(null);
    setCopied(false);
    onClose();
  };

  if (!isOpen) return null;

  // Show link copy view after creating invitation via link method
  if (createdInvitation && inviteMethod === 'link') {
    return (
      <>
        <div className="fixed inset-0 bg-black/50 z-50" onClick={handleClose} />
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="bg-ui-background-secondary rounded-lg shadow-xl w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-ui-border-primary">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-600 dark:text-green-400" />
                <h2 className="text-lg font-semibold text-ui-text-primary">
                  Invitation Created
                </h2>
              </div>
              <button
                onClick={handleClose}
                className="p-1 rounded hover:bg-ui-background-tertiary transition-colors"
              >
                <X className="w-5 h-5 text-ui-text-tertiary" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4 space-y-4">
              <p className="text-sm text-ui-text-secondary">
                Share this link with <span className="font-medium text-ui-text-primary">{createdInvitation.email}</span> to invite them as a {createdInvitation.role}.
              </p>

              {/* Link Display */}
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={createdInvitation.invite_url || ''}
                  readOnly
                  className="flex-1 px-3 py-2 text-sm border border-ui-border-primary rounded-lg 
                             bg-ui-background-tertiary text-ui-text-primary"
                />
                <button
                  onClick={handleCopyLink}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors flex items-center gap-2
                             ${copied 
                               ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' 
                               : 'bg-brand-primary hover:bg-brand-primaryDark text-black'}`}
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </button>
              </div>

              <p className="text-xs text-ui-text-tertiary">
                This link expires on {new Date(createdInvitation.expires_at).toLocaleDateString()}.
              </p>

              {/* Done Button */}
              <div className="flex justify-end pt-2">
                <button
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-black bg-brand-primary hover:bg-brand-primaryDark rounded-lg transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-50"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-ui-background-secondary rounded-lg shadow-xl w-full max-w-md"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-ui-border-primary">
            <div className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-brand-primary dark:text-brand-primaryDarkMode" />
              <h2 className="text-lg font-semibold text-ui-text-primary">
                Invite Member
              </h2>
            </div>
            <button
              onClick={handleClose}
              className="p-1 rounded hover:bg-ui-background-tertiary transition-colors"
            >
              <X className="w-5 h-5 text-ui-text-tertiary" />
            </button>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 space-y-4">
            <p className="text-sm text-ui-text-secondary">
              Invite someone to join <span className="font-medium text-ui-text-primary">{organizationName}</span>
            </p>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </div>
            )}

            {/* Invite Method Toggle */}
            <div>
              <label className="block text-sm font-medium text-ui-text-secondary mb-2">
                How do you want to invite?
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setInviteMethod('email')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border transition-colors
                             ${inviteMethod === 'email'
                               ? 'border-brand-primary bg-brand-primary/10 dark:bg-brand-primary/20 text-brand-primary dark:text-brand-primaryDarkMode'
                               : 'border-ui-border-primary text-ui-text-secondary hover:bg-ui-background-tertiary'
                             }`}
                >
                  <Mail className="w-4 h-4" />
                  <span className="text-sm font-medium">Send Email</span>
                </button>
                <button
                  type="button"
                  onClick={() => setInviteMethod('link')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border transition-colors
                             ${inviteMethod === 'link'
                               ? 'border-brand-primary bg-brand-primary/10 dark:bg-brand-primary/20 text-brand-primary dark:text-brand-primaryDarkMode'
                               : 'border-ui-border-primary text-ui-text-secondary hover:bg-ui-background-tertiary'
                             }`}
                >
                  <Link2 className="w-4 h-4" />
                  <span className="text-sm font-medium">Copy Link</span>
                </button>
              </div>
            </div>

            {/* Email Input */}
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-ui-text-secondary mb-1"
              >
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ui-text-tertiary" />
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="colleague@company.com"
                  className="w-full pl-10 pr-4 py-2 border border-ui-border-primary rounded-lg 
                             bg-ui-background-secondary text-ui-text-primary
                             placeholder-ui-text-tertiary dark:placeholder-ui-text-tertiary
                             focus:ring-2 focus:ring-brand-primary focus:border-transparent
                             disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isSubmitting}
                  autoFocus
                />
              </div>
              {inviteMethod === 'link' && (
                <p className="mt-1 text-xs text-ui-text-tertiary">
                  The invitation will be tied to this email address.
                </p>
              )}
            </div>

            {/* Role Select */}
            <div>
              <label
                htmlFor="role"
                className="block text-sm font-medium text-ui-text-secondary mb-1"
              >
                Role
              </label>
              <select
                id="role"
                value={role}
                onChange={(e) => setRole(e.target.value as 'admin' | 'member' | 'viewer')}
                className="w-full px-4 py-2 border border-ui-border-primary rounded-lg 
                           bg-ui-background-secondary text-ui-text-primary
                           focus:ring-2 focus:ring-brand-primary focus:border-transparent
                           disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
                <option value="viewer">Viewer (Read-only)</option>
              </select>
              <p className="mt-1 text-xs text-ui-text-tertiary">
                {role === 'admin'
                  ? 'Admins can manage members and organization settings.'
                  : role === 'viewer'
                    ? 'Viewers can only view data. They cannot start scans, create fixes, or change settings.'
                    : 'Members can view and work on projects.'}
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="px-4 py-2 text-sm font-medium text-ui-text-secondary
                           hover:bg-ui-background-tertiary rounded-lg transition-colors"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !email.trim()}
                className="px-4 py-2 text-sm font-medium text-black bg-brand-primary hover:bg-brand-primaryDark 
                           disabled:opacity-50 rounded-lg transition-colors
                           flex items-center gap-2 disabled:cursor-not-allowed"
              >
                {isSubmitting ? (
                  <>
                    <Spinner size={16} tone="current" />
                    {inviteMethod === 'email' ? 'Sending...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    {inviteMethod === 'email' ? (
                      <>
                        <Mail className="w-4 h-4" />
                        Send Invitation
                      </>
                    ) : (
                      <>
                        <Link2 className="w-4 h-4" />
                        Create Link
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default InviteMemberModal;
